import {
  GetWeightedKeywordsAndPhrasesForProductResponse,
  KeywordPerformanceScores,
  WeightedKeywordsAndPhrasesSection
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { ChartDataAdapter, KeywordsMetadata, SectionKeywords } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { KeywordBreakdownData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { getItemHighlightsDisplayConfig } from 'src/utils/app';

const BASE_EXPECTED_SECTIONS = ['Title', 'Bullet 1', 'Bullet 2', 'Bullet 3', 'Bullet 4', 'Bullet 5', 'Description'];
const ITEM_HIGHLIGHTS_SECTION = 'Item Highlights';

const getExpectedSections = (includeItemHighlights: boolean) =>
  includeItemHighlights
    ? ['Title', ITEM_HIGHLIGHTS_SECTION, 'Bullet 1', 'Bullet 2', 'Bullet 3', 'Bullet 4', 'Bullet 5', 'Description']
    : BASE_EXPECTED_SECTIONS;

export class ProductLevelKeywordsDetailsAdapter implements ChartDataAdapter<KeywordBreakdownData> {
  private weightedKeywordsAndPhrasesResponse: GetWeightedKeywordsAndPhrasesForProductResponse;

  private summaryResponse: KeywordPerformanceScores;

  constructor(
    weightedKeywordsAndPhrasesResponse: GetWeightedKeywordsAndPhrasesForProductResponse,
    summaryResponse: KeywordPerformanceScores
  ) {
    this.weightedKeywordsAndPhrasesResponse = weightedKeywordsAndPhrasesResponse;
    this.summaryResponse = summaryResponse;
  }

  transform(): KeywordBreakdownData {
    const { showItemHighlights } = getItemHighlightsDisplayConfig();
    const expectedSections = getExpectedSections(showItemHighlights);

    const getSectionPlaceholder = (sectionName: string) => {
      return {
        name: sectionName,
        content: [{ phrase: `Missing required ${sectionName.toLocaleLowerCase()}.` }]
      };
    };

    const data = this.weightedKeywordsAndPhrasesResponse;

    if (!data.isSuccess) {
      // TODO(tyan): handle error - return an array of sections with empty content
      return { sections: expectedSections.map(getSectionPlaceholder), summaryScores: {} as KeywordPerformanceScores };
    }

    const transformSection = (section: WeightedKeywordsAndPhrasesSection): SectionKeywords => {
      const groups = section.groups.reduce((acc, group) => {
        return {
          ...acc,
          [group.groupId]: {
            totalWeight: group.totalSubcatWeight,
            weights: group.weights
          }
        };
      }, {} as Record<string, KeywordsMetadata>);

      const content = section.content.map((phrase) => {
        if (phrase.groupId) {
          return {
            phrase: phrase.phrase,
            metadata: groups[phrase.groupId]
          };
        }

        return phrase;
      });

      return {
        name: section.name,
        content:
          content && content.length > 0
            ? content
            : [{ phrase: `This product has no ${section.name.toLocaleLowerCase()}.` }]
      };
    };

    const reorderSections = (sections: SectionKeywords[]) => {
      const bulletSections = sections
        .filter((s) => s.name.startsWith('Bullet '))
        .map((s) => s.name)
        .sort((a, b) => {
          const aNum = parseInt(a.split(' ')[1], 10);
          const bNum = parseInt(b.split(' ')[1], 10);
          return aNum - bNum;
        });

      const orderedSectionNames = showItemHighlights
        ? ['Title', ITEM_HIGHLIGHTS_SECTION, ...bulletSections, 'Description']
        : ['Title', ...bulletSections, 'Description'];

      return orderedSectionNames.map(
        (sectionName) => sections.find((s) => s.name === sectionName) || getSectionPlaceholder(sectionName)
      );
    };

    const sections = data?.weightedKeywordPhrases?.weightedKeywordPhrases?.sections ?? [];

    const transformedSections = sections
      .filter((section) => showItemHighlights || section.name !== ITEM_HIGHLIGHTS_SECTION)
      .map(transformSection);
    return { sections: reorderSections(transformedSections), summaryScores: this.summaryResponse };
  }
}
